import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import { startTestCheckout, verifyTestCheckout, startLiveCheckout, verifyLiveCheckout,
  reconcileLiveAccount, checkoutAvailability, type CheckoutMode } from '@/lib/paystack';

const benefits = [
  '12 AI teaching videos per month',
  'Longer and deeper AI lessons when the topic requires it',
  'No ads during video generation',
  'All LAVYN study tools included',
  'Future Premium improvements as LAVYN grows',
];

function livePaymentMessage(result: {
  paymentStatus: 'success'|'pending'|'failed'|'review';
  premiumActivated: boolean;
}) {
  if (result.paymentStatus === 'review') {
    return 'Paystack confirmed the payment, but Premium needs a support review. Do not pay again while this is being resolved.';
  }
  if (result.paymentStatus === 'failed') {
    return 'Paystack confirmed that this payment failed. No Premium access was added; you can start a new checkout.';
  }
  if (result.paymentStatus === 'pending') {
    return 'Paystack has not confirmed a final result yet. This checkout remains reserved; do not start another payment. Check again shortly.';
  }
  return result.premiumActivated
    ? 'Payment verified. Premium is active for the verified subscription period.'
    : 'Payment status was recorded without changing account access. Contact support before making another payment.';
}

export default function PremiumPage() {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [showCheckoutNotice, setShowCheckoutNotice] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState('');
  const [paying, setPaying] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState<CheckoutMode | null>(null);

  useEffect(() => {
    let active = true;
    checkoutAvailability().then(mode => { if (active) setCheckoutMode(mode); })
      .catch(() => { if (active) { setShowCheckoutNotice(true); setPaymentMessage('Checkout is currently unavailable. No payment was started.'); } });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (checkoutMode !== 'live') return;
    const params = new URLSearchParams(window.location.search);
    if (params.has('paystack_live_reference') || params.has('paystack_reference')) return;
    let active = true;
    reconcileLiveAccount().then(result => {
      if (!active || result.paymentStatus === 'none' && !result.hasActivePremium) return;
      setShowCheckoutNotice(true);
      if (result.paymentStatus === 'review') {
        setPaymentMessage('Paystack confirmed a payment that needs support review. Do not pay again while it is being resolved.');
      } else if (result.paymentStatus === 'pending') {
        setPaymentMessage('Paystack has not confirmed a final result. The existing checkout remains reserved; do not pay again yet.');
      } else if (result.paymentStatus === 'failed') {
        setPaymentMessage('Paystack confirmed the previous payment failed. Its reservation is released; you can start a new checkout.');
      } else if (result.hasActivePremium) {
        const source = result.premiumSource === 'test' ? 'from a TEST payment'
          : result.premiumSource === 'live' ? 'from a verified LIVE payment'
          : result.premiumSource === 'manual' ? 'from an existing account grant' : '';
        setPaymentMessage(`Premium is already active${source ? ` ${source}` : ''}. No new payment was started.`);
      } else if (result.paymentStatus === 'success' && result.premiumActivated) {
        setPaymentMessage('The existing Paystack payment was verified and Premium is active.');
      }
    }).catch(() => {
      if (active) {
        setShowCheckoutNotice(true);
        setPaymentMessage('The existing payment could not yet be reconciled. No new payment was started; try checking again shortly.');
      }
    });
    return () => { active = false; };
  }, [checkoutMode]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const liveReference = params.get('paystack_live_reference');
    const reference = liveReference || params.get('paystack_reference');
    if (!reference) return;
    let active=true;
    setShowCheckoutNotice(true);
    setPaymentMessage(liveReference ? 'Verifying your payment…' : 'Verifying your test payment…');
    (liveReference ? verifyLiveCheckout(reference) : verifyTestCheckout(reference)).then(result => {
      if(active) setPaymentMessage(liveReference
        ? livePaymentMessage(result)
        : result.premiumActivated
          ? 'Test payment verified. Your designated test account has Premium access for the verified period. No real payment was taken.'
          : 'Test payment verified. Existing account access was preserved; no additional Premium grant was applied.');
    }).catch(error => {if(active) setPaymentMessage(error instanceof Error ? error.message : 'Payment verification failed. Please retry.');});
    return ()=>{active=false;};
  }, []);

  const upgrade = async () => {
    if(paying || !checkoutMode) return;
    setPaying(true); setShowCheckoutNotice(true);
    setPaymentMessage(checkoutMode === 'live' ? 'Opening secure Paystack checkout for R89.99/month.' : 'Opening Paystack TEST checkout. No real money will be charged.');
    try {
      const params = new URLSearchParams(window.location.search);
      const liveReference = params.get('paystack_live_reference');
      const reference=liveReference || params.get('paystack_reference');
      if(reference) {
        const result=await (liveReference ? verifyLiveCheckout(reference) : verifyTestCheckout(reference));
        setPaymentMessage(liveReference
          ? livePaymentMessage(result)
          : result.premiumActivated
            ? 'Test payment verified. Test Premium access is active.'
            : 'Payment verified. Existing account access was preserved.');
      } else if (checkoutMode === 'live') {
        const result=await startLiveCheckout();
        if(result && result.paymentStatus === 'pending') {
          const url=new URL(window.location.href);
          url.searchParams.set('paystack_live_reference',result.reference);
          window.history.replaceState({},'',url.href);
          setPaymentMessage(livePaymentMessage({paymentStatus:'pending',premiumActivated:false}));
        }
      }
      else await startTestCheckout();
    } catch(error) {
      setPaymentMessage(error instanceof Error ? error.message : 'Checkout is unavailable. Please retry.');
    } finally {setPaying(false);}
  };

  const reconcileExistingPayment = async () => {
    if (reconciling) return;
    setReconciling(true);
    setShowCheckoutNotice(true);
    setPaymentMessage('Checking Paystack’s status for the existing checkout…');
    try {
      const result = await reconcileLiveAccount();
      if (result.paymentStatus === 'review') {
        setPaymentMessage('Paystack confirmed a payment that needs support review. Do not pay again while it is being resolved.');
      } else if (result.paymentStatus === 'pending') {
        setPaymentMessage('Paystack still reports this checkout as pending. It remains reserved; do not pay again yet.');
      } else if (result.paymentStatus === 'failed') {
        setPaymentMessage('Paystack confirmed the previous payment failed. Its reservation is released; you can start a new checkout.');
      } else if (result.hasActivePremium) {
        const source = result.premiumSource === 'test' ? 'from a TEST payment'
          : result.premiumSource === 'live' ? 'from a verified LIVE payment'
          : result.premiumSource === 'manual' ? 'from an existing account grant' : '';
        setPaymentMessage(`Premium is already active${source ? ` ${source}` : ''}. No new payment was started.`);
      } else if (result.paymentStatus === 'success' && result.premiumActivated) {
        setPaymentMessage('The existing Paystack payment was verified and Premium is active.');
      } else {
        setPaymentMessage('No active Premium entitlement or unresolved payment was found. You can retry checkout.');
      }
    } catch (error) {
      setPaymentMessage(error instanceof Error ? error.message : 'The existing payment could not yet be reconciled.');
    } finally {
      setReconciling(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') navigate('/profile');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  const muted = isDark ? 'text-[#A1A1A6]' : 'text-[#737378]';
  const panel = isDark
    ? 'border-white/[0.09] bg-[#1b1c20]/80 shadow-[0_24px_80px_-48px_rgba(0,0,0,0.8)]'
    : 'border-black/[0.07] bg-white/75 shadow-[0_24px_80px_-48px_rgba(26,26,30,0.22)]';
  const subtle = isDark ? 'border-white/[0.07] bg-white/[0.035]' : 'border-black/[0.055] bg-black/[0.018]';

  return (
    <main
      data-premium-page
      aria-labelledby="premium-pricing-title"
      className={`relative min-h-full flex-1 overflow-y-auto px-4 pb-[calc(104px+env(safe-area-inset-bottom,0px))] pt-4 transition-colors duration-500 sm:px-7 sm:pt-7 lg:px-10 lg:pb-12 ${
        isDark ? 'bg-[#101113] text-[#F4F4F5]' : 'bg-[#f7f7f8] text-[#202023]'
      }`}
    >
      <div className="mx-auto w-full max-w-[820px]">
        <motion.button
          type="button"
          aria-label="Back to Settings"
          onClick={() => navigate('/profile')}
          className={`mb-4 inline-flex h-10 items-center gap-2 rounded-full px-3.5 text-[13px] font-medium transition-colors ${
            isDark ? 'text-[#a7a7ac] hover:bg-white/[0.06] hover:text-white' : 'text-[#66666b] hover:bg-black/[0.045] hover:text-[#222225]'
          }`}
          whileTap={{ scale: 0.97 }}
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          <span>Settings</span>
        </motion.button>

        <motion.section
          className={`overflow-hidden rounded-[28px] border backdrop-blur-2xl ${panel}`}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="px-5 pb-5 pt-6 sm:px-9 sm:pb-7 sm:pt-9">
            <div className="mb-4 flex items-center gap-2.5">
              <span className={`flex h-8 w-8 items-center justify-center rounded-[11px] ${isDark ? 'bg-white text-[#171719]' : 'bg-[#252528] text-white'}`}>
                <i className="ri-vip-crown-line text-[15px]" aria-hidden="true" />
              </span>
              <span className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${muted}`}>A little more room to learn</span>
            </div>
            <h1 id="premium-pricing-title" className="text-[29px] font-semibold leading-[1.1] tracking-[-0.045em] sm:text-[38px]">
              LAVYN Premium
            </h1>
            <p className={`mt-2 text-[14px] leading-relaxed sm:text-[15px] ${muted}`}>
              Get more from your AI study tutor.
            </p>

            <div className={`my-5 h-px sm:my-6 ${isDark ? 'bg-white/[0.08]' : 'bg-black/[0.07]'}`} />

            <ul className="space-y-3.5 sm:space-y-4" aria-label="Premium benefits">
              {benefits.map((benefit, index) => (
                <motion.li
                  key={benefit}
                  className="flex items-start gap-3.5"
                  initial={{ opacity: 0, x: -7 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + index * 0.055, duration: 0.28 }}
                >
                  <span className={`mt-[1px] flex h-[21px] w-[21px] shrink-0 items-center justify-center rounded-full ${
                    isDark ? 'bg-white/[0.1] text-[#ededee]' : 'bg-[#242427]/[0.07] text-[#38383c]'
                  }`}>
                    <i className="ri-check-line text-[12px]" aria-hidden="true" />
                  </span>
                  <span className={`text-[13px] leading-[1.55] sm:text-[14px] ${isDark ? 'text-[#e3e3e5]' : 'text-[#38383d]'}`}>
                    {benefit}
                  </span>
                </motion.li>
              ))}
            </ul>

            <div className={`my-5 h-px sm:my-6 ${isDark ? 'bg-white/[0.08]' : 'bg-black/[0.07]'}`} />

            <div className="flex flex-wrap items-end justify-between gap-x-5 gap-y-3">
              <div>
                <p className={`mb-1 text-[10px] font-semibold uppercase tracking-[0.17em] ${muted}`}>Monthly</p>
                <p className="flex items-baseline gap-1.5">
                  <span className="text-[34px] font-semibold leading-none tracking-[-0.05em] sm:text-[40px]">R89.99</span>
                  <span className={`text-[13px] ${muted}`}>/ month</span>
                </p>
              </div>
              <span className={`pb-1 text-[11px] ${muted}`}>{checkoutMode === 'live'
                ? 'Monthly subscription · secure Paystack checkout'
                : checkoutMode === 'test' ? 'Live subscriptions unavailable · TEST accounts only' : 'Checkout unavailable'}</span>
            </div>

            <AnimatePresence>
              {showCheckoutNotice && (
                <motion.div
                  role="status"
                  aria-live="polite"
                  className={`mt-4 rounded-2xl border px-4 py-3 text-[12px] leading-relaxed ${
                    isDark ? 'border-white/[0.1] bg-white/[0.045] text-[#c5c5c9]' : 'border-black/[0.08] bg-white/80 text-[#5d5d62]'
                  }`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                >
                  {paymentMessage || 'Checkout is currently unavailable.'}
                </motion.div>
              )}
            </AnimatePresence>

            <motion.button
              type="button"
              onClick={upgrade}
              disabled={paying || !checkoutMode}
              whileTap={{ scale: 0.985 }}
              className={`mt-4 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-[15px] px-5 text-[14px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                isDark
                  ? 'bg-[#f0f0f1] text-[#18181a] hover:bg-white focus-visible:ring-white focus-visible:ring-offset-[#1b1c20]'
                  : 'bg-[#242427] text-white hover:bg-[#111113] focus-visible:ring-[#45454a] focus-visible:ring-offset-white'
              }`}
            >
              {paying ? 'Please wait…' : 'Upgrade to Premium'}
              <i className="ri-arrow-right-line text-[15px]" aria-hidden="true" />
            </motion.button>

            {checkoutMode === 'live' && paymentMessage.includes('unresolved checkout prevents another payment') && (
              <button
                type="button"
                onClick={reconcileExistingPayment}
                disabled={reconciling}
                className={`mt-3 min-h-11 w-full rounded-[14px] border px-4 text-[13px] font-medium transition-colors disabled:opacity-60 ${
                  isDark ? 'border-white/[0.12] text-[#e3e3e5] hover:bg-white/[0.05]' : 'border-black/[0.1] text-[#38383d] hover:bg-black/[0.03]'
                }`}
              >
                {reconciling ? 'Checking existing payment…' : 'Check existing payment'}
              </button>
            )}

            <p className={`mt-3 text-center text-[10px] leading-relaxed ${muted}`}>
              Premium improvements are planned as LAVYN grows. Free and Premium video allowances are unchanged.
            </p>
          </div>
          <div className={`flex items-center gap-3 border-t px-5 py-3.5 sm:px-9 ${subtle}`}>
            <i className={`ri-lock-2-line text-[13px] ${muted}`} aria-hidden="true" />
            <p className={`text-[10px] leading-relaxed ${muted}`}>
              {checkoutMode === 'live'
                ? 'Paystack processes your payment securely. Premium activates only after server-side payment verification.'
                : 'Live subscriptions are unavailable. Designated test accounts can use Paystack TEST checkout without real charges.'}
            </p>
          </div>
        </motion.section>
      </div>
    </main>
  );
}
